import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MapContainer, Circle, CircleMarker, Marker, Polygon, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getRequestWithToken } from "../../utils/ApiRequests";
import { geoJsonToLeaflet, geoPointToLeaflet, getPolygonCenter, isPointInPolygon } from "../../utils/geoUtils";
import { checkPermission } from "../../utils/userPermissions";
import Unauthorized from "../../components/unauthorized/Unauthorized";
import ReusableLoader from "../../components/ReusableLoader/ReusableLoader";

// Palette of visually distinct, map-friendly colors — one per unique role
const ROLE_COLOR_PALETTE = [
  "#e53935", "#8e24aa", "#1e88e5", "#00897b",
  "#43a047", "#f4511e", "#6d4c41", "#039be5",
  "#3949ab", "#00acc1", "#7cb342", "#fb8c00",
];

// Assigns a stable color to each role on first encounter
const _roleColorCache = {};
let _roleColorIndex = 0;
function getRoleColor(role) {
  const key = role || "unknown";
  if (!_roleColorCache[key]) {
    _roleColorCache[key] = ROLE_COLOR_PALETTE[_roleColorIndex % ROLE_COLOR_PALETTE.length];
    _roleColorIndex += 1;
  }
  return _roleColorCache[key];
}

// Returns up to 2 initials: first letter of each of the first two words
function getRoleInitials(role) {
  if (!role) return "?";
  const words = role.trim().split(/[\s_-]+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Builds a Leaflet divIcon — a colored circle with role initials
// Pass overrideColor to force inside/outside coloring, otherwise uses role palette
function createRoleMarkerIcon(role, overrideColor) {
  const color = overrideColor || getRoleColor(role);
  const initials = getRoleInitials(role);
  return L.divIcon({
    className: "",
    html: `<div style="
      background:${color};
      width:32px;height:32px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-weight:700;font-size:11px;
      border:2px solid #fff;
      box-shadow:0 2px 6px rgba(0,0,0,0.45);
      font-family:sans-serif;line-height:1;
    ">${initials}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18],
  });
}

const LIMIT_OPTIONS = [100, 500, 1000, 5000, 10000, 50000];
const RADIUS_MARKS = [
  { value: 5, label: "5km" },
  { value: 25, label: "25km" },
  { value: 50, label: "50km" },
  { value: 100, label: "100km" },
  { value: 200, label: "200km" },
];

// Computes a simple arithmetic centroid from Leaflet polygon positions
function computeCentroid(positions) {
  // Handle MultiPolygon: positions[0][0] is [lat,lng], else positions[0] is [lat,lng]
  const ring = Array.isArray(positions[0][0]) ? positions[0] : positions;
  let latSum = 0, lngSum = 0;
  ring.forEach(([lat, lng]) => { latSum += lat; lngSum += lng; });
  return [latSum / ring.length, lngSum / ring.length];
}

// ---------------------------------------------------------------------------
// Helper component: re-fits map bounds whenever the polygon changes
// ---------------------------------------------------------------------------
function FitBoundsToPolygon({ positions }) {
  const map = useMap();

  useEffect(() => {
    if (!positions || positions.length === 0) return;

    try {
      // Flatten nested arrays for MultiPolygon so we can build a bounds object
      const flatPositions = Array.isArray(positions[0][0])
        ? positions.flat(1)
        : positions;

      if (flatPositions.length > 0) {
        const bounds = L.latLngBounds(flatPositions);
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [30, 30] });
        }
      }
    } catch (_) {
      // silently ignore if bounds computation fails
    }
  }, [positions, map]);

  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const RegionMapView = () => {
  const canRead = checkPermission("HC-Addon");

  // ---- data state ----
  const [regions, setRegions] = useState([]);
  const [mappings, setMappings] = useState([]);
  // all candidates returned by the single radius API call
  const [allCandidates, setAllCandidates] = useState([]);
  // true once the user has clicked "Load Candidates" at least once for the current region
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);

  // ---- selection state ----
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  // ---- candidate limit ----
  const [candidateLimit, setCandidateLimit] = useState(999999);

  // ---- radius (km) centred on polygon centroid ----
  const [radiusKm, setRadiusKm] = useState(25);

  // ---- loading / error state ----
  const [initialLoading, setInitialLoading] = useState(true);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep a ref to abort stale candidate fetch on rapid button clicks
  const candidateFetchCounter = useRef(0);

  // ---- fetch regions + roles on mount ----
  useEffect(() => {
    let cancelled = false;

    const fetchInitial = async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const [regionsRes, mappingsRes] = await Promise.all([
          getRequestWithToken("/admin/regions?page=1&limit=200"),
          getRequestWithToken("/admin/region-form-mappings?&page=1&limit=200"),
        ]);
        // if (!cancelled) {
        //   setRegions(regionsRes?.data?.data?.regions || []);
        //   setMappings(mappingsRes?.data?.data?.mappings || []);
        // }
        if (!cancelled) {
          const sortedRegions = (regionsRes?.data?.data?.regions || []).slice().sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          );
          const sortedMappings = (mappingsRes?.data?.data?.mappings || []).slice().sort((a, b) =>
            (a.form_id?.role || "").localeCompare(b.form_id?.role || "")
          );
          setRegions(sortedRegions);
          setMappings(sortedMappings);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.msg || err?.message || "Failed to load initial data.");
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    fetchInitial();
    return () => { cancelled = true; };
  }, []);

  // ---- single API call: radius-based fetch triggered by button click ----
  const handleLoadCandidates = useCallback(async () => {
    if (!selectedRegion) return;

    const fetchId = ++candidateFetchCounter.current;
    setCandidatesLoading(true);
    setError(null);
    setCandidatesLoaded(false);

    try {
      const polygonPos = geoJsonToLeaflet(selectedRegion.polygon);
      const [centerLat, centerLng] = computeCentroid(polygonPos);
      const roleParam = selectedRole ? `&role=${encodeURIComponent(selectedRole.role)}` : "";
      const url = `/admin/plus-attribute-candidate?centerLat=${centerLat}&centerLng=${centerLng}&radius=${radiusKm}&page=1&limit=${candidateLimit}${roleParam}`;

      const res = await getRequestWithToken(url);

      if (fetchId === candidateFetchCounter.current) {
        setAllCandidates(res?.data?.data || []);
        setCandidatesLoaded(true);
      }
    } catch (err) {
      if (fetchId === candidateFetchCounter.current) {
        setError(err?.response?.data?.msg || err?.message || "Failed to load candidates.");
        setAllCandidates([]);
      }
    } finally {
      if (fetchId === candidateFetchCounter.current) {
        setCandidatesLoading(false);
      }
    }
  }, [selectedRegion, selectedRole, radiusKm, candidateLimit]);

  // ---- derived role options: filtered by selected region from mappings ----
  const roleOptions = selectedRegion
    ? mappings
      .filter((m) => m.region_id?._id === selectedRegion._id)
      .map((m) => m.form_id)
      .filter(Boolean)
    : [];

  // ---- derived map data ----
  const polygonPositions = selectedRegion?.polygon
    ? geoJsonToLeaflet(selectedRegion.polygon)
    : [];

  const mapCenter = getPolygonCenter(polygonPositions);

  // Centroid used as circle center — recomputed when region changes
  const centroid = polygonPositions.length > 0 ? computeCentroid(polygonPositions) : null;

  // Client-side inside/outside split using ray-casting on the raw GeoJSON polygon
  const insideMarkers = [];
  const outsideMarkers = [];
  allCandidates.forEach((c) => {
    const pos = geoPointToLeaflet(c?.location?.coordinates);
    if (!pos) return;
    const isInside = isPointInPolygon(c.location.coordinates, selectedRegion?.polygon);
    if (isInside) {
      insideMarkers.push({ id: c._id, position: pos, role: c.role });
    } else {
      outsideMarkers.push({ id: c._id, position: pos, role: c.role });
    }
  });

  const totalInView = insideMarkers.length + outsideMarkers.length;

  // ---- render guards ----
  if (!canRead) return <Unauthorized />;
  if (initialLoading) return <ReusableLoader />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 80px)", gap: 2 }}>
      {/* Page header */}
      <Typography variant="h5" fontWeight={600}>
        Region Map View
      </Typography>

      {/* Filter bar — row 1: dropdowns */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Autocomplete

          options={regions}
          getOptionLabel={(option) => option.name || ""}
          value={selectedRegion}
          onChange={(_, value) => {
            setSelectedRegion(value);
            setSelectedRole(null);
            setAllCandidates([]);
            setCandidatesLoaded(false);
          }}
          sx={{ minWidth: 340 }}
          renderInput={(params) => (
            <TextField {...params} label="Select Region" size="small" />
          )}
        />

        <Autocomplete
          size="small"
          options={roleOptions}
          getOptionLabel={(option) => option.role || ""}
          value={selectedRole}
          onChange={(_, value) => {
            setSelectedRole(value);
            setAllCandidates([]);
            setCandidatesLoaded(false);
          }}
          disabled={!selectedRegion}
          sx={{ minWidth: 340 }}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Role (optional)" size="small" />
          )}
        />

        {/* <Autocomplete
          size="small"
          options={LIMIT_OPTIONS}
          getOptionLabel={(opt) => opt.toLocaleString()}
          value={candidateLimit}
          onChange={(_, value) => { if (value !== null) setCandidateLimit(value); }}
          disableClearable
          sx={{ minWidth: 160 }}
          renderInput={(params) => (
            <TextField {...params} label="Max Candidates" size="small" />
          )}
        /> */}

        {selectedRegion && (
          <Button
            variant="contained"
            size="medium"
            onClick={handleLoadCandidates}
            disabled={candidatesLoading}
            sx={{ whiteSpace: "nowrap" }}
          >
            {candidatesLoading ? "Loading…" : "Load Candidates"}
          </Button>
        )}

        {candidatesLoading && <CircularProgress size={20} />}
      </Box>

      {/* Filter bar — row 2: radius slider */}
      {selectedRegion && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          <Tooltip title="Expand a circle around the region centroid to find candidates just outside the polygon boundary">
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              Expansion radius: <strong>{radiusKm} km</strong>
            </Typography>
          </Tooltip>
          <Slider
            value={radiusKm}
            min={1}
            max={70}
            step={1}
            marks={RADIUS_MARKS}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v} km`}
            onChange={(_, v) => setRadiusKm(v)}
            sx={{ maxWidth: 420 }}
          />
        </Box>
      )}

      {/* Error banner */}
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/* Stats bar */}
      {selectedRegion && candidatesLoaded && !candidatesLoading && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Tooltip title="Candidates whose location falls inside the polygon boundary (client-side ray-cast)">
            <Chip
              label={`✅ Inside polygon: ${insideMarkers.length}`}
              sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, border: "1px solid #81c784" }}
            />
          </Tooltip>
          <Tooltip title={`Candidates within ${radiusKm} km of region centroid but outside the polygon`}>
            <Chip
              label={`⚠️ Outside polygon (within ${radiusKm} km): ${outsideMarkers.length}`}
              sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600, border: "1px solid #ffb74d" }}
            />
          </Tooltip>
          <Chip
            label={`Total in view: ${totalInView}`}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          {totalInView > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round((insideMarkers.length / totalInView) * 100)}% coverage inside polygon
            </Typography>
          )}
        </Box>
      )}

      {/* Map */}
      <Box sx={{ flexGrow: 1, borderRadius: 1, overflow: "hidden", border: "1px solid #e0e0e0" }}>
        <MapContainer
          center={mapCenter}
          zoom={selectedRegion ? 10 : 5}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {polygonPositions.length > 0 && (
            <>
              <FitBoundsToPolygon positions={polygonPositions} />
              <Polygon
                positions={polygonPositions}
                pathOptions={{ color: "#1976d2", fillColor: "#42a5f5", fillOpacity: 0.25 }}
              />
            </>
          )}

          {/* Radius circle centred on polygon centroid — updates live with the slider */}
          {centroid && (
            <>
              <Circle
                center={centroid}
                radius={radiusKm * 1000}
                pathOptions={{ color: "#ff9800", fillColor: "#ffe0b2", fillOpacity: 0.15, dashArray: "6 4" }}
              />
              {/* Centroid dot */}
              <CircleMarker
                center={centroid}
                radius={6}
                pathOptions={{ color: "#e65100", fillColor: "#ff9800", fillOpacity: 1, weight: 2 }}
              />
            </>
          )}

          {/* Inside polygon — green markers */}
          {insideMarkers.map(({ id, position, role }) => (
            <Marker key={`in-${id}`} position={position} icon={createRoleMarkerIcon(role, "#2e7d32")} />
          ))}

          {/* Outside polygon but within radius — orange markers */}
          {outsideMarkers.map(({ id, position, role }) => (
            <Marker key={`out-${id}`} position={position} icon={createRoleMarkerIcon(role, "#e65100")} />
          ))}
        </MapContainer>
      </Box>
    </Box>
  );
};

export default RegionMapView;
